import React, { useEffect, useMemo, useRef, useState, ChangeEvent } from 'react';
import { Props as AdditionalProps, SelectProps } from '../../Components/Form/Select/Select';
import { ApiGeoUnitWithDayConstraints } from '@ibe/api';
import { Input } from 'reactstrap';
import { useClickAway } from 'react-use';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NestedGeoUnitsSelectOption from './NestedGeoUnitsSelectOption';

const NestedGeoUnitsSelect = (
  props: AdditionalProps<ApiGeoUnitWithDayConstraints> & SelectProps<ApiGeoUnitWithDayConstraints>
): JSX.Element => {
  const { options: propsOptions, onChange, selectedOption, placeholder, isDisabled } = props;
  const optionsFromProps = useMemo(
    () => propsOptions.sort((a, b) => a.name.localeCompare(b.name)),
    [propsOptions]
  );
  const parentTypeCode = optionsFromProps[0] ? optionsFromProps[0].typeCode : '';

  const [options, setOptions] = useState<ApiGeoUnitWithDayConstraints[]>(optionsFromProps);
  const [inputValue, setInputValue] = useState<string>('');
  const [selectMenuOpen, setSelectMenuOpen] = useState<boolean>(false);
  const [inputHasSelectedValue, setInputHasSelectedValue] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [openParents, setOpenParents] = useState<Record<string, number>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const onBlur = (): void => {
    setSelectMenuOpen(false);
    if (!!selectedOption) {
      setInputValue(selectedOption.name);
      setInputHasSelectedValue(true);
    } else {
      setInputValue('');
    }
    setOptions([...optionsFromProps]);
    setOpenParents({});
  };

  useEffect(() => {
    if (optionsFromProps.length > 0) {
      setOptions(optionsFromProps);
    }
  }, [optionsFromProps]);

  useEffect(() => {
    onBlur();
  }, [selectedOption]);

  useClickAway(containerRef, () => {
    onBlur();
  });

  const openParent = (
    newOptions: ApiGeoUnitWithDayConstraints[],
    option: ApiGeoUnitWithDayConstraints,
    indexInCurrentOptions: number
  ): ApiGeoUnitWithDayConstraints[] => {
    const newOptionsCopy = [...newOptions];
    setOpenParents(value => ({ ...value, [option.code]: Object.keys(option.children).length }));
    newOptionsCopy.splice(
      indexInCurrentOptions + 1,
      0,
      ...Object.entries(option.children)
        .map(([, geoUnit]) => geoUnit as ApiGeoUnitWithDayConstraints)
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    return newOptionsCopy;
  };

  const closeParent = (
    newOptions: ApiGeoUnitWithDayConstraints[],
    option: ApiGeoUnitWithDayConstraints,
    indexInCurrentOptions: number
  ): ApiGeoUnitWithDayConstraints[] => {
    const newOptionsCopy = [...newOptions];
    const newOpenParents = { ...openParents };
    newOptionsCopy.splice(indexInCurrentOptions + 1, newOpenParents[option.code]);
    delete newOpenParents[option.code];
    setOpenParents(newOpenParents);
    return newOptionsCopy;
  };

  const handleOptionClick = (option: ApiGeoUnitWithDayConstraints): void => {
    if (Object.keys(option.children).length > 0) {
      let newOptions = [...options];
      const indexInCurrentOptions = options.findIndex(item => item.code === option.code);
      if (!Object.keys(openParents).includes(option.code)) {
        newOptions = [...openParent(newOptions, option, indexInCurrentOptions)];
      } else {
        newOptions = [...closeParent(newOptions, option, indexInCurrentOptions)];
      }
      setOptions(newOptions);
    } else {
      onChange(option);
      setSelectMenuOpen(false);
      setInputValue(option.name);
      setInputHasSelectedValue(true);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { value: newValue }
    } = e;
    const value = inputHasSelectedValue ? newValue.substring(0, 1) : newValue;
    setOpenParents({});
    if (!!value) {
      let newOptions = [...optionsFromProps];
      optionsFromProps.forEach(parent => {
        if (
          Object.keys(parent.children).length > 0 &&
          !!Object.entries(parent.children).find(([, geoUnit]) =>
            geoUnit.name.toLowerCase().includes(value.toLowerCase())
          )
        ) {
          newOptions = [
            ...openParent(
              [...newOptions],
              {
                ...parent,
                children: Object.fromEntries(
                  Object.entries(parent.children).filter(([, geoUnit]) =>
                    geoUnit.name.toLowerCase().includes(value.toLowerCase())
                  )
                )
              },
              newOptions.findIndex(item => item.code === parent.code)
            )
          ];
        } else if (
          Object.keys(parent.children).length > 0 &&
          !parent.name.toLowerCase().includes(value.toLowerCase())
        ) {
          newOptions.splice(
            newOptions.findIndex(option => option.code === parent.code),
            1
          );
        }
      });
      setOptions(newOptions);
    } else {
      setOptions([...optionsFromProps]);
    }
    setInputValue(value);
    setInputHasSelectedValue(false);
  };

  const handleInputFocus = (): void => {
    setTimeout(() => {
      if (inputRef.current && inputHasSelectedValue) {
        inputRef.current.setSelectionRange(0, 0);
      }
    }, 60);
  };

  const handleInputClick = (): void => {
    setSelectMenuOpen(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
    if (inputHasSelectedValue) {
      handleInputFocus();
    }
  };

  return (
    <>
      <div ref={containerRef} className="nested-geounits-select">
        <Input
          innerRef={inputRef}
          placeholder={placeholder}
          isdisabled={isDisabled ? 'true' : 'false'}
          value={inputValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onFocus={handleInputFocus}
        />
        {selectMenuOpen && (
          <div className="nested-geounits-select__menu">
            <div ref={scrollContainerRef} className="nested-geounits-select__menu__inner">
              {options.map(option => (
                <NestedGeoUnitsSelectOption
                  key={option.id}
                  option={option}
                  onClick={handleOptionClick}
                  parentTypeCode={parentTypeCode}
                  selectedOption={selectedOption}
                  scrollContainerRef={scrollContainerRef}
                  openParents={openParents}
                />
              ))}
            </div>
          </div>
        )}
        <FontAwesomeIcon
          icon={faAngleDown}
          className={`nested-geounits-select__caret${
            selectMenuOpen ? ' nested-geounits-select__caret--open' : ''
          }`}
          onClick={handleInputClick}
        />
      </div>
    </>
  );
};

export default NestedGeoUnitsSelect;
